import { createFileRoute, Link } from "@tanstack/react-router";
import { REVIEWS } from "@/lib/reviews";
import { Star } from "lucide-react";
import { BackButton } from "@/components/site/BackButton";
import { brandAggregateRatingJsonLd } from "@/lib/seo";

export const Route = createFileRoute("/reviews")({
  head: () => ({
    meta: [
      { title: "Customer Stories — KAKAIYS" },
      {
        name: "description",
        content:
          "Letters from the people who wear KAKAIYS. Real customers, real lives, leather that has become a part of who they are.",
      },
      { property: "og:title", content: "Customer Stories — KAKAIYS" },
      {
        property: "og:description",
        content: "Letters from the people who wear KAKAIYS.",
      },
      { property: "og:image", content: REVIEWS[0].image },
      { name: "twitter:image", content: REVIEWS[0].image },
      { property: "og:url", content: "https://kakaiys.com/reviews" },
    ],
    links: [{ rel: "canonical", href: "https://kakaiys.com/reviews" }],
    scripts: [
      {
        type: "application/ld+json",
        children: brandAggregateRatingJsonLd({ ratingValue: 5.0, reviewCount: 2400 }),
      },
    ],
  }),
  component: ReviewsPage,
});

function ReviewsPage() {
  return (
    <>
      <section className="pt-32 md:pt-40 pb-12 container-luxe text-center">
        <BackButton fallback="/" className="mb-8" />
        <div className="eyebrow mb-4 text-gold">In Their Words</div>
        <h1 className="font-serif text-5xl md:text-7xl">
          Letters from the <span className="italic text-gradient-gold text-slate-50">wearers.</span>
        </h1>
        <p className="mt-6 max-w-2xl mx-auto text-sm md:text-base text-muted-foreground">
          We do not run sponsored campaigns. We do not pay for reviews. What follows is what
          our customers have written to us, in their own words, about the leather they have
          chosen to live in.
        </p>
        <div className="mt-8 flex justify-center items-center gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star key={i} className="h-4 w-4 fill-gold text-gold" />
          ))}
          <span className="ml-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
            5.0 — from 2,400+ owners
          </span>
        </div>
      </section>

      <section className="container-luxe py-12 md:py-20">
        <div className="space-y-20 md:space-y-32 max-w-6xl mx-auto">
          {REVIEWS.map((r, i) => (
            <article
              key={r.name}
              className={`grid md:grid-cols-2 gap-10 md:gap-16 items-center ${
                i % 2 === 1 ? "md:[direction:rtl]" : ""
              }`}
            >
              <div className="relative aspect-[4/5] overflow-hidden img-frame md:[direction:ltr]">
                <img
                  src={r.image}
                  alt={`${r.name} wearing the ${r.product}`}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </div>
              <div className="md:[direction:ltr]">
                <div className="flex items-center gap-1 mb-5">
                  {Array.from({ length: r.rating }).map((_, k) => (
                    <Star key={k} className="h-4 w-4 fill-gold text-gold" />
                  ))}
                </div>
                <blockquote className="font-serif text-2xl md:text-3xl leading-snug text-foreground">
                  "{r.body}"
                </blockquote>
                <div className="mt-8 hairline w-12" />
                <div className="mt-6">
                  <div className="font-serif text-lg">{r.name}</div>
                  <div className="text-xs uppercase tracking-[0.24em] text-muted-foreground mt-1">
                    {r.city}
                  </div>
                  <div className="text-xs text-gold mt-3">
                    {r.product}
                    {r.yearsWearing ? ` · ${r.yearsWearing}` : ""}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="container-luxe py-24 md:py-32 text-center border-t border-border">
        <div className="eyebrow mb-4">Become Part of It</div>
        <h2 className="font-serif text-4xl md:text-5xl max-w-2xl mx-auto">
          The next letter <span className="italic text-gradient-gold text-slate-50">could be yours.</span>
        </h2>
        <div className="mt-10">
          <Link
            to="/shop"
            className="btn-gold inline-flex items-center justify-center px-8 py-4 text-[11px] uppercase tracking-[0.28em]"
          >
            Find Your Piece
          </Link>
        </div>
      </section>
    </>
  );
}
